import imageCompression from 'browser-image-compression';
import type { DistrictPhotoConfig } from '@citizen-walk/shared-types';

/**
 * 投稿写真の前処理 + サムネ生成 (共有モジュール)。
 *
 * もとは `apps/user/src/features/posts/photo.ts` にあったが、**管理者の「写真まとめて一括投稿」
 * (apps/admin) でも参加者投稿と完全に同一のサイズ/品質で処理する**ため shared-ui に移管した
 * (issue #28 follow-up、2026-06-08)。apps 同士は import できないため共有先は packages/* に限る。
 * user の `photo.ts` は本モジュールの re-export shim になっている (既存 import は無改修)。
 *
 *   - `browser-image-compression` library を使用 (canvas API 経由で EXIF 自動 strip)
 *   - `useWebWorker: false` で固定 (Worker 実装は CDN fetch で機内モード破綻、UX 優先)
 *   - 地区別 `photoConfig` で圧縮パラメータを上書き可能化 (master 設定)。
 *     `uploadOriginal=true` なら元ファイルを無圧縮でそのまま返す (リサイズ/再エンコード無し = EXIF 保持)。
 */

// 既定値 (photoConfig 未設定 / 各 field 未指定時の fallback、現行挙動を維持)。
export const PROCESSED_MAX_WIDTH = 1600;
export const PROCESSED_MAX_MB = 1.5;
export const PROCESSED_QUALITY = 0.85;
export const DEFAULT_MAX_UPLOAD_MB = 10;
const THUMB_MAX_WIDTH = 400;
const THUMB_MAX_MB = 0.2;
const THUMB_QUALITY = 0.8;

/**
 * 投稿写真を前処理する。`config.uploadOriginal=true` のときは無圧縮で元ファイルをそのまま返す
 * (リサイズ・再エンコードなし。EXIF も保持される点に注意)。それ以外は config の値 (未指定は既定値)
 * で圧縮・リサイズする。
 */
export interface PreprocessPhotoOptions {
  /**
   * 360度 (全天球 / 正距円筒) 画像として処理する (docs/27 §3)。既定の 1600px リサイズでは見回し表示に
   * 粗すぎるため、幅 4096px (2:1 なので 4096x2048)・上限 5MB まで許容する。地区設定
   * (`maxWidthOrHeight` / `maxSizeMB`) がそれより大きければ地区設定を優先する。
   */
  equirectangular?: boolean;
}

/** 360度画像の前処理サイズ既定 (2:1 を保ったまま 4096x2048 まで。1600px 既定では粗すぎる)。 */
export const EQUIRECT_MAX_WIDTH = 4096;
/** 360度画像の圧縮後サイズ上限 (MB)。4096x2048 の JPEG q0.85 が概ね 2〜3MB に収まる。 */
export const EQUIRECT_MAX_MB = 5;

export async function preprocessPhoto(
  file: File,
  config?: DistrictPhotoConfig,
  options?: PreprocessPhotoOptions,
): Promise<File> {
  if (config?.uploadOriginal) {
    // HEIC / HEIF はサーバー (写真のぼかし処理) で読めないため、元画像をそのまま上げる地区でも
    // JPEG に変換する (大きさは変えない。撮影情報は消える、docs/28 レビュー S2)。変換できない端末
    // (HEIC を表示できないブラウザ) では投稿前に知らせる。
    if (isHeic(file)) {
      try {
        return await imageCompression(file, {
          maxSizeMB: 50,
          maxWidthOrHeight: 16384,
          useWebWorker: false,
          fileType: 'image/jpeg',
          initialQuality: 0.92,
        });
      } catch {
        throw new Error(
          'この写真の形式 (HEIC) はこの端末では投稿できません。カメラの設定で「互換性優先」(JPEG) にして撮影してください。',
        );
      }
    }
    return file;
  }
  const baseMaxMB = config?.maxSizeMB ?? PROCESSED_MAX_MB;
  const baseMaxWidth = config?.maxWidthOrHeight ?? PROCESSED_MAX_WIDTH;
  const equirect = options?.equirectangular === true;
  return await imageCompression(file, {
    maxSizeMB: equirect ? Math.max(baseMaxMB, EQUIRECT_MAX_MB) : baseMaxMB,
    maxWidthOrHeight: equirect ? Math.max(baseMaxWidth, EQUIRECT_MAX_WIDTH) : baseMaxWidth,
    useWebWorker: false,
    fileType: 'image/jpeg',
    initialQuality: config?.quality ?? PROCESSED_QUALITY,
  });
}

/** HEIC / HEIF の写真か (MIME か拡張子で判定。端末によって MIME が空のことがある)。 */
export function isHeic(file: { type?: string; name?: string }): boolean {
  const type = (file.type ?? '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif') return true;
  return /\.(heic|heif)$/i.test(file.name ?? '');
}

/** photoConfig から受付上限 (bytes) を解決 (未設定 / 不正値は既定 10MB)。 */
export function resolveMaxUploadBytes(config?: DistrictPhotoConfig): number {
  const mb =
    typeof config?.maxUploadSizeMB === 'number' && config.maxUploadSizeMB > 0
      ? config.maxUploadSizeMB
      : DEFAULT_MAX_UPLOAD_MB;
  return mb * 1024 * 1024;
}

export interface PhotoSizeCheck {
  ok: boolean;
  maxMB: number;
  actualMB: number;
}

/** アップロード前の最終ファイル (圧縮後 or 元画像) を受付上限と照合する (pure)。 */
export function checkPhotoSize(file: Blob, config?: DistrictPhotoConfig): PhotoSizeCheck {
  const maxBytes = resolveMaxUploadBytes(config);
  return {
    ok: file.size <= maxBytes,
    maxMB: maxBytes / (1024 * 1024),
    actualMB: file.size / (1024 * 1024),
  };
}

/** checkPhotoSize が ok=false のときに throw する標準エラー (呼出側が catch して toast)。 */
export function assertPhotoSize(file: Blob, config?: DistrictPhotoConfig): void {
  const r = checkPhotoSize(file, config);
  if (!r.ok) {
    throw new Error(
      `画像サイズが大きすぎます (上限 ${r.maxMB.toFixed(0)}MB、ファイル ${r.actualMB.toFixed(1)}MB)。`,
    );
  }
}

export async function generateThumbnail(file: File): Promise<File> {
  return await imageCompression(file, {
    maxSizeMB: THUMB_MAX_MB,
    maxWidthOrHeight: THUMB_MAX_WIDTH,
    useWebWorker: false,
    fileType: 'image/jpeg',
    initialQuality: THUMB_QUALITY,
  });
}
